import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Row,
  Col,
  Statistic,
  Space,
  Tooltip,
  Menu,
  Typography,
  Empty,
  Rate,
  Descriptions,
  Divider,
  Alert
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  StarOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as actions from 'store/actions/AuthActions';
import useUrlFilters from 'hooks/useUrlFilters';

const { TextArea } = Input;
const { Sider, Content } = Layout;
const { Text } = Typography;

// Section and pagination live in the URL (pr_ prefix: this is a tab of My Profile).
const URL_FILTERS = {
  section: { type: 'string', default: 'reviews' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const PerformanceReview = ({ isProfileLocked = false, employee }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [acknowledgeModalVisible, setAcknowledgeModalVisible] = useState(false);
  const [acknowledgeComment, setAcknowledgeComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: 'pr_' });
  const activeSection = urlFilters.section;
  const setActiveSection = (value) => setUrlFilters({ section: value });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // Fetch reviews
  const fetchReviews = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await actions.loadMyReviews(page, pageSize);
      if (response) {
        setReviews(response.results || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count || 0
        });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews(urlFilters.page, Math.min(urlFilters.pageSize, MAX_PAGE_SIZE));
  }, [urlFilters.page, urlFilters.pageSize]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'default';
      case 'SUBMITTED':
        return 'processing';
      case 'ACKNOWLEDGED':
        return 'success';
      case 'COMPLETED':
        return 'green';
      default:
        return 'default';
    }
  };

  // Get rating description
  const getRatingDescription = (rating) => {
    const descriptions = {
      1: 'Needs Improvement',
      2: 'Below Expectations',
      3: 'Meets Expectations',
      4: 'Exceeds Expectations',
      5: 'Outstanding'
    };
    return descriptions[rating] || '-';
  };

  // Handle view review
  const handleViewReview = async (record) => {
    try {
      const fullReview = await actions.fetchEmployeeReview(record.id);
      if (fullReview) {
        setSelectedReview(fullReview);
        setViewModalVisible(true);
      }
    } catch (error) {
      console.error('Error fetching review details:', error);
    }
  };

  // Handle acknowledge modal open
  const handleAcknowledgeOpen = (review) => {
    setSelectedReview(review);
    setAcknowledgeComment('');
    setAcknowledgeModalVisible(true);
  };

  // Handle acknowledge submit
  const handleAcknowledge = async () => {
    try {
      setSubmitting(true);
      await actions.acknowledgeReview(selectedReview.id, acknowledgeComment || null);
      setAcknowledgeModalVisible(false);
      setViewModalVisible(false);
      fetchReviews(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error acknowledging review:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle table pagination change
  const handleTableChange = (newPagination) => {
    // A new page size starts again from page 1
    const sizeChanged = newPagination.pageSize !== urlFilters.pageSize;
    setUrlFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
  };

  // Calculate statistics
  const getReviewStats = () => {
    const pending = reviews.filter(r => r.status === 'SUBMITTED').length;
    const completed = reviews.filter(r => r.status === 'ACKNOWLEDGED' || r.status === 'COMPLETED').length;
    const avgRating = reviews.filter(r => r.overall_rating).reduce((acc, r) => acc + r.overall_rating, 0) /
                      (reviews.filter(r => r.overall_rating).length || 1);
    return { pending, completed, avgRating: avgRating.toFixed(1) };
  };

  const stats = getReviewStats();

  // Table columns
  const columns = [
    {
      title: 'Review Type',
      dataIndex: 'review_type_display',
      key: 'review_type',
      width: 150,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text || record.review_type}</Text>
          {record.title && <Text type="secondary" style={{ fontSize: '12px' }}>{record.title}</Text>}
        </Space>
      )
    },
    {
      title: 'Period',
      key: 'period',
      width: 200,
      render: (_, record) => (
        <span>
          {dayjs(record.review_period_start).format('MMM DD, YYYY')} - {dayjs(record.review_period_end).format('MMM DD, YYYY')}
        </span>
      )
    },
    {
      title: 'Reviewer',
      dataIndex: 'reviewer_name',
      key: 'reviewer',
      width: 150,
      render: (text) => text || <Text type="secondary">-</Text>
    },
    {
      title: 'Rating',
      dataIndex: 'overall_rating',
      key: 'rating',
      width: 150,
      render: (rating) => rating ? (
        <Space>
          <Rate disabled defaultValue={rating} style={{ fontSize: '14px' }} />
          <Text type="secondary">({rating}/5)</Text>
        </Space>
      ) : <Text type="secondary">Not rated</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => (
        <Tag color={getStatusColor(status)}>
          {record.status_display || status}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewReview(record)}
            />
          </Tooltip>
          {record.status === 'SUBMITTED' && (
            <Tooltip title="Acknowledge Review">
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleAcknowledgeOpen(record)}
              >
                Acknowledge
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  // Sidebar menu items
  const menuItems = [
    {
      key: 'reviews',
      icon: <StarOutlined />,
      label: 'My Reviews'
    }
  ];

  // Render review details modal content
  const renderReviewDetails = () => {
    if (!selectedReview) return null;

    return (
      <div>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Review Type" span={1}>
            {selectedReview.review_type_display || selectedReview.review_type}
          </Descriptions.Item>
          <Descriptions.Item label="Status" span={1}>
            <Tag color={getStatusColor(selectedReview.status)}>
              {selectedReview.status_display || selectedReview.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Review Period" span={2}>
            {dayjs(selectedReview.review_period_start).format('MMMM DD, YYYY')} - {dayjs(selectedReview.review_period_end).format('MMMM DD, YYYY')}
          </Descriptions.Item>
          {selectedReview.title && (
            <Descriptions.Item label="Title" span={2}>
              {selectedReview.title}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Reviewer" span={2}>
            {selectedReview.reviewer_name || '-'}
          </Descriptions.Item>
        </Descriptions>

        <Divider>Ratings</Divider>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Overall Rating"
                value={selectedReview.overall_rating || '-'}
                suffix={selectedReview.overall_rating ? '/5' : ''}
                valueStyle={{ color: '#1890ff' }}
                prefix={<TrophyOutlined />}
              />
              {selectedReview.overall_rating && (
                <Text type="secondary">{getRatingDescription(selectedReview.overall_rating)}</Text>
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Performance"
                value={selectedReview.performance_rating || '-'}
                suffix={selectedReview.performance_rating ? '/5' : ''}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Communication"
                value={selectedReview.communication_rating || '-'}
                suffix={selectedReview.communication_rating ? '/5' : ''}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Teamwork"
                value={selectedReview.teamwork_rating || '-'}
                suffix={selectedReview.teamwork_rating ? '/5' : ''}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Punctuality"
                value={selectedReview.punctuality_rating || '-'}
                suffix={selectedReview.punctuality_rating ? '/5' : ''}
              />
            </Card>
          </Col>
        </Row>

        <Divider>Feedback</Divider>

        {selectedReview.strengths && (
          <Card size="small" title="Strengths" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.strengths}</p>
          </Card>
        )}

        {selectedReview.areas_for_improvement && (
          <Card size="small" title="Areas for Improvement" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.areas_for_improvement}</p>
          </Card>
        )}

        {selectedReview.goals && (
          <Card size="small" title="Goals" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.goals}</p>
          </Card>
        )}

        {selectedReview.reviewer_comments && (
          <Card size="small" title="Reviewer Comments" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.reviewer_comments}</p>
          </Card>
        )}

        {selectedReview.employee_comments && (
          <Card size="small" title="Your Comments" style={{ marginBottom: '16px' }}>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{selectedReview.employee_comments}</p>
          </Card>
        )}

        {selectedReview.status === 'SUBMITTED' && (
          <>
            <Divider />
            <Alert
              message="Action Required"
              description="Please acknowledge this review to confirm you have read and understood the feedback."
              type="info"
              showIcon
              action={
                <Button
                  type="primary"
                  onClick={() => {
                    setViewModalVisible(false);
                    handleAcknowledgeOpen(selectedReview);
                  }}
                >
                  Acknowledge Now
                </Button>
              }
            />
          </>
        )}

        {selectedReview.acknowledged_at && (
          <Text type="secondary" style={{ display: 'block', marginTop: '16px' }}>
            Acknowledged on: {dayjs(selectedReview.acknowledged_at).format('MMMM DD, YYYY hh:mm A')}
          </Text>
        )}
      </div>
    );
  };

  // Render reviews section
  const renderReviewsSection = () => (
    <div style={{ padding: '0 24px 24px 24px' }}>
      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Pending Acknowledgment"
              value={stats.pending}
              valueStyle={{ color: stats.pending > 0 ? '#faad14' : '#52c41a' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Completed Reviews"
              value={stats.completed}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Average Rating"
              value={stats.avgRating !== 'NaN' ? stats.avgRating : '-'}
              suffix={stats.avgRating !== 'NaN' ? '/5' : ''}
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Pending Reviews Alert */}
      {stats.pending > 0 && (
        <Alert
          message={`You have ${stats.pending} review(s) pending acknowledgment`}
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Reviews Table */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>My Performance Reviews</span>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={reviews}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} reviews`
          }}
          onChange={handleTableChange}
          size="middle"
          locale={{
            emptyText: (
              <Empty
                description="No reviews yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
        />
      </Card>
    </div>
  );

  return (
    <Layout style={{ background: 'transparent', height: '100%' }}>
      <Sider
        width={220}
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0'
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[activeSection]}
          style={{ height: '100%', borderRight: 0, paddingTop: '16px' }}
          items={menuItems}
          onClick={({ key }) => setActiveSection(key)}
        />
      </Sider>

      <Content style={{ background: '#f5f5f5', overflowY: 'auto' }}>
        <div style={{ padding: '24px 0' }}>
          {renderReviewsSection()}
        </div>
      </Content>

      {/* View Review Modal */}
      <Modal
        title="Review Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {renderReviewDetails()}
      </Modal>

      {/* Acknowledge Modal */}
      <Modal
        title="Acknowledge Review"
        open={acknowledgeModalVisible}
        onCancel={() => setAcknowledgeModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setAcknowledgeModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="acknowledge" type="primary" loading={submitting} onClick={handleAcknowledge}>
            Acknowledge Review
          </Button>
        ]}
        width={600}
      >
        <Alert
          message="By acknowledging this review, you confirm that you have read and understood the feedback provided."
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />

        <Form layout="vertical">
          <Form.Item label="Your Comments (Optional)">
            <TextArea
              rows={4}
              placeholder="Add any comments or feedback you'd like to share..."
              value={acknowledgeComment}
              onChange={(e) => setAcknowledgeComment(e.target.value)}
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default PerformanceReview;
